import { utils } from '@hh/global.js'

/**
 * ex usage
 * assign id = your id
 * <hh-swiper data-id={your id}>
 *  <div class='swiper swiper--{{your id}}`> // .swiper for styles, .swiper--{{id}} for js
 *    <div class='swiper-wrapper'>
 *      <div class='swiper-slide' </div>
 *    </div>
 *    <div class='swiper-button-prev swiper-button-prev--{{your id}}></div>
 *   <div class='swiper-button-next swiper-button-next--{{your id}}></div>
 *  </div>
 * </hh-swiper>
 *
 *
 * use data-mobile-nav="true" to enable navigation on mobile (ie, the product card images and announcement bar don't show arrows on mobile)
 * data-slides-per-view should be a comma separated string of three values for mobile, tablet, and desktop. ex: "1,2,3"
 * data-space-between should be a comma separated string of three values for mobile, tablet, and desktop. ex: "1,2,3"
 * data-custom-next and data-custom-previous can be used to specify custom classes for the next and previous buttons. See the `product-feed` section for how it's done
 * data-effect can be fade, cube, flip.. all options from swiper.js. Default is slide
 * data-pdp-media-gallery="true" enables the PDP zoom button for the active slide
 */

const openPdpMediaModal = (modalId, mediaId) => {
  document.body.setAttribute(
    'modal',
    JSON.stringify({ id: modalId, element: mediaId })
  )
}

const updatePdpZoomTrigger = (swiperEl, trigger) => {
  if (!trigger) return

  const activeSlide = swiperEl.querySelector('.swiper-slide-active')
  const mediaId = activeSlide?.dataset.mediaId
  const isImage = activeSlide?.dataset.mediaType === 'image'

  if (!isImage || !mediaId) {
    trigger.classList.add('hidden')
    return
  }

  const slides = [...swiperEl.querySelectorAll('.swiper-slide')]
  const index = slides.indexOf(activeSlide) + 1

  trigger.classList.remove('hidden')
  trigger.dataset.mediaId = mediaId
  trigger.setAttribute(
    'aria-label',
    window.accessibilityStrings.openMedia.replace('__INDEX__', index)
  )
}

const setSlidesA11y = (swiper) => {
  const isPdpMediaGallery =
    swiper.closest('hh-swiper')?.dataset?.pdpMediaGallery === 'true'
  setTimeout(() => {
    swiper.querySelector('.swiper-notification')?.remove()
    swiper.querySelector('.swiper-wrapper').removeAttribute('aria-live')
    if (swiper.classList.contains('swiper--mini-cart-rec-products')) {
      document.querySelectorAll('.js-cart-arrow')?.forEach((el) => {
        el.removeAttribute('aria-controls')
      })
    }
    if (swiper.dataset.productCardMedia == 'true') {
      const arrows = swiper.querySelectorAll('.swiper-button')
      arrows.forEach((arrow) => {
        arrow.removeAttribute('aria-controls')
      })
    }
  }, 0)

  setTimeout(() => {
    const visibleSlides = swiper.querySelectorAll('.swiper-slide-visible')
    const hasVisibleSlide = visibleSlides.length > 0
    const hasActiveSlide =
      isPdpMediaGallery &&
      swiper.querySelector('.swiper-slide-active') != null
    // Skip when container is hidden (e.g. in a hidden tab): Swiper won't mark any slide visible,
    // and we must not apply inert to all slides or links become unclickable.
    if ((!hasVisibleSlide && !hasActiveSlide) || !swiper.offsetParent) return

    swiper.querySelectorAll('.swiper-slide').forEach((slide) => {
      const isVisible =
        slide.classList.contains('swiper-slide-visible') ||
        (isPdpMediaGallery && slide.classList.contains('swiper-slide-active'))

      if (isVisible) {
        slide.removeAttribute('inert')
        slide.removeAttribute('aria-hidden')
        const focusable = slide.querySelectorAll('a, hh-button, button')
        focusable.forEach((el) => {
          el.removeAttribute('tabindex')
        })
      } else {
        slide.setAttribute('inert', '')
        slide.setAttribute('aria-hidden', 'true')
        const focusable = slide.querySelectorAll('a, hh-button, button')
        focusable.forEach((el) => {
          el.setAttribute('tabindex', '-1')
        })
      }
    })
  }, 0)

  if (swiper.closest('hh-swiper').dataset?.overrideButtonControls) {
    swiper
      .querySelectorAll('.swiper-button-prev, .swiper-button-next')
      ?.forEach((button) => {
        setTimeout(() => {
          button.removeAttribute('aria-controls')
          if (button.classList.contains('swiper-button-next')) {
            button.setAttribute('aria-label', 'Next set of slides')
          } else if (button.classList.contains('swiper-button-prev')) {
            button.setAttribute('aria-label', 'Previous set of slides')
          }
        }, 0)
      })
  }
}

class HHSwiper extends HTMLElement {
  static observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.initSwiper()
          observer.unobserve(entry.target)
        }
      })
    },
    { rootMargin: '200px' }
  )

  connectedCallback() {
    HHSwiper.observer.observe(this)
  }

  initSwiper() {
    if (this.swiper) return

    this.swiper = new Swiper(
      this.querySelector(`.swiper--${this.dataset.id}`),
      {
        direction: this.dataset.verticalScroll ? 'vertical' : 'horizontal',
        loop: !Boolean(this.dataset.cancelLoop),
        effect: this.dataset.effect ? this.dataset.effect : 'slide',
        fadeEffect: {
          crossFade: true,
        },
        watchSlidesProgress: true,
        mousewheel: Boolean(this.dataset.mousewheel),
        navigation: {
          nextEl: this.dataset.customNext
            ? document.querySelector(this.dataset.customNext)
            : this.querySelector(`.swiper-button-next--${this.dataset.id}`),
          prevEl: this.dataset.customPrevious
            ? document.querySelector(this.dataset.customPrevious)
            : this.querySelector(`.swiper-button-prev--${this.dataset.id}`),
          enabled: Boolean(this.dataset.mobileNav),
        },
        slidesPerView: this.dataset.slidesPerView
          ? parseFloat(this.dataset.slidesPerView.split(',')[0])
          : 1,
        slidesPerGroup: this.dataset.slidesPerGroup
          ? parseFloat(this.dataset.slidesPerGroup.split(',')[0])
          : 1,
        scrollbar: {
          el: `.swiper-scrollbar--${this.dataset.id}`,
          enabled: true,
        },
        spaceBetween: this.dataset.spaceBetween
          ? parseFloat(this.dataset.spaceBetween.split(',')[0])
          : 0,
        pagination: {
          el: `.swiper-pagination--${this.dataset.id}`,
          ...(this.dataset.accessiblePagination
            ? {
                clickable: true,
                renderBullet: function (index, className) {
                  return (
                    '<button type="button" class="' +
                    className +
                    '" aria-label="' +
                    window.accessibilityStrings.goToSlide.replace(
                      '{{ index }}',
                      index + 1
                    ) +
                    '"></button>'
                  )
                },
              }
            : {}),
        },
        breakpoints: {
          750: {
            scrollbar: {
              enabled: false,
            },
            navigation: {
              enabled: true,
            },
            slidesPerView: this.dataset.slidesPerView
              ? parseFloat(this.dataset.slidesPerView.split(',')[1])
              : 1,
            slidesPerGroup: this.dataset.slidesPerGroup
              ? parseFloat(this.dataset.slidesPerGroup.split(',')[1])
              : 1,
            spaceBetween: this.dataset.spaceBetween
              ? parseFloat(this.dataset.spaceBetween.split(',')[1])
              : 0,
          },
          990: {
            scrollbar: {
              enabled: false,
            },
            navigation: {
              enabled: true,
            },
            slidesPerView: this.dataset.slidesPerView
              ? parseFloat(this.dataset.slidesPerView.split(',')[2])
              : 1,
            slidesPerGroup: this.dataset.slidesPerGroup
              ? parseFloat(this.dataset.slidesPerGroup.split(',')[2])
              : 1,
            spaceBetween: this.dataset.spaceBetween
              ? parseFloat(this.dataset.spaceBetween.split(',')[0])
              : 0,
          },
        },
        autoplay: false,
        nested: Boolean(this.dataset.nested),
        ...(this.dataset.slideLabelMessage
          ? {
              a11y: {
                slideLabelMessage: this.dataset.slideLabelMessage,
              },
            }
          : {}),
        on: {
          init() {
            setSlidesA11y(this.el)
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
            }
          },
          slideChange() {
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
            }
          },
          slideChangeTransitionEnd() {
            setSlidesA11y(this.el)
            if (this.el.closest('hh-swiper')?.dataset?.pdpMediaGallery) {
              updatePdpZoomTrigger(
                this.el,
                this.el.querySelector('.js-pdp-zoom-trigger')
              )
            }
          },
        },
      }
    )

    if (this.dataset.pdpInfoOnLast) {
      this.swiper.on('sliderMove', function (pointermove) {
        const activeIndex = this.activeIndex
        const slidesLength = this.slides.length - 1
        const userSwipingNext =
          pointermove.swipeDirection == 'next' ? true : false

        if (activeIndex == slidesLength && userSwipingNext) {
          document.body.classList.remove('not-swiped')
          document.body.classList.add('swiped-up')
        }
      })
    }

    this.dispatchEvent(
      new CustomEvent('swiper:init', {
        bubbles: true,
        detail: { swiper: this.swiper, id: this.dataset.id },
      })
    )

    if (this.dataset.pdpMediaGallery) {
      const zoomTrigger = this.querySelector('.js-pdp-zoom-trigger')
      const modalId = this.dataset.modalId

      const swiperEl = this.querySelector(`.swiper--${this.dataset.id}`)

      zoomTrigger?.addEventListener('click', () => {
        const activeSlide = swiperEl?.querySelector('.swiper-slide-active')
        const mediaId = activeSlide?.dataset.mediaId
        const isImage = activeSlide?.dataset.mediaType === 'image'

        if (isImage && mediaId && modalId) {
          openPdpMediaModal(modalId, mediaId)
        }
      })
    }

    if (this.dataset.mobileOnly) {
      const swiperResponsiveToggle = () => {
        const isDesktop = window.innerWidth > 800

        if (isDesktop && this.swiper.initialized) {
          this.swiper.destroy(false, true)
        } else if (!isDesktop && this.swiper.destroyed) {
          this.swiper.init()
        }
      }

      swiperResponsiveToggle()
      const throttledResize = utils.throttle(swiperResponsiveToggle, 100)
      window.addEventListener('resize', throttledResize)
    }
  }

  disconnectedCallback() {
    HHSwiper.observer.unobserve(this)
    this.swiper?.destroy()
    this.swiper = null
  }
}

customElements.define('hh-swiper', HHSwiper)
